// src/components/FilteredUsersManagement.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import Layout from "../Layout";
const FilteredUsersManagement = ({ initialOrgFilter }) => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(initialOrgFilter || "");
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for edit and delete dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({
    email: "",
    full_name: "",
    role: "",
    organization_name: "",
  });

  // Fetch organizations for dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/organizations`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch organizations");
        const data = await response.json();
        setOrganizations(data.organizations || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchOrganizations();
  }, []);

  // Fetch users based on filter and pagination
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (selectedOrg) params.append("organization_id", selectedOrg);
        params.append("page", page + 1); // backend pages are 1-indexed
        params.append("limit", rowsPerPage);
        const url = `${
          process.env.REACT_APP_API_NEW_HOST_URL
        }/athrv/api/v1/user?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch users");
        const data = await response.json();
        setUsers(data.users || []);
        setTotalUsers(data.total || 0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [selectedOrg, page, rowsPerPage]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditData({
      email: user.email || "",
      full_name: `${user.fname || ""} ${user.lname || ""}`.trim(),
      role: user.role || "",
    });
    setOpenEditDialog(true);
  };
  

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleEditSave = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/user/${selectedUser.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify({
            full_name: editData.full_name.trim(),
            role: editData.role,
          }),
        }
      );
  
      if (!response.ok) throw new Error('Failed to update user');
  
      setOpenEditDialog(false);
      setSelectedUser(null);
  
      // Refresh user list
      const params = new URLSearchParams();
      if (selectedOrg) params.append('organization_id', selectedOrg);
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);
  
      const url = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/user?${params.toString()}`;
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
  
      const data = await resp.json();
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/user/${selectedUser.user_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete user");
      setOpenDeleteDialog(false);
      setSelectedUser(null);
      // Refresh list
      const params = new URLSearchParams();
      if (selectedOrg) params.append("organization_id", selectedOrg);
      params.append("page", page + 1);
      params.append("limit", rowsPerPage);
      const url = `${
        process.env.REACT_APP_API_NEW_HOST_URL
      }/athrv/api/v1/user?${params.toString()}`;
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      const data = await resp.json();
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Layout>
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        User Management
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Select
          value={selectedOrg}
          onChange={(e) => {
            setSelectedOrg(e.target.value);
            setPage(0);
          }}
          displayEmpty
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">
            <em>All Organizations</em>
          </MenuItem>
          {organizations.map((org) => (
            <MenuItem key={org.organization_id} value={org.organization_id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.organization_name}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleEditClick(user)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(user)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            value={editData.email}
            onChange={(e) =>
              setEditData({ ...editData, email: e.target.value })
            }
            disabled
          />
          <TextField
            margin="dense"
            label="Full Name"
            fullWidth
            value={editData.full_name}
            onChange={(e) =>
              setEditData({ ...editData, full_name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Role"
            fullWidth
            value={editData.role}
            onChange={(e) => setEditData({ ...editData, role: e.target.value })}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user {selectedUser?.email}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Layout>
  );
};

export default FilteredUsersManagement;